import type { LinkView } from "./api";

export interface WaitForLinkRunResultOptions {
  linkId: string;
  runId: string;
  loadLinks: () => Promise<LinkView[]>;
  intervalMs?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
}

export interface LinkRunTarget {
  linkId: string;
  runId: string;
}

export interface WaitForMultipleLinkRunResultsOptions {
  runs: LinkRunTarget[];
  loadLinks: () => Promise<LinkView[]>;
  intervalMs?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
}

export type LinkRunPollResult =
  | { status: "completed"; link: LinkView; links: LinkView[] }
  | { status: "missing"; links: LinkView[] }
  | { status: "timeout"; links: LinkView[] };

export type MultipleLinkRunPollResult =
  | { status: "completed"; links: LinkView[] }
  | { status: "missing"; missingRuns: LinkRunTarget[]; links: LinkView[] }
  | { status: "timeout"; pendingRuns: LinkRunTarget[]; links: LinkView[] };

const DEFAULT_INTERVAL_MS = 2000;
const DEFAULT_MAX_ATTEMPTS = 180;

export async function waitForLinkRunResult(options: WaitForLinkRunResultOptions): Promise<LinkRunPollResult> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  const sleep = options.sleep ?? defaultSleep;
  let latestLinks: LinkView[] = [];

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    latestLinks = await options.loadLinks();
    const link = latestLinks.find((item) => item.id === options.linkId);
    if (!link) {
      return { status: "missing", links: latestLinks };
    }
    if (link.lastCheckRunId === options.runId) {
      return { status: "completed", link, links: latestLinks };
    }
    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return { status: "timeout", links: latestLinks };
}

export async function waitForMultipleLinkRunResults(
  options: WaitForMultipleLinkRunResultsOptions
): Promise<MultipleLinkRunPollResult> {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  const sleep = options.sleep ?? defaultSleep;
  let latestLinks: LinkView[] = [];

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    latestLinks = await options.loadLinks();
    const missingRuns = missingRunTargets(options.runs, latestLinks);
    if (missingRuns.length > 0) {
      return { status: "missing", missingRuns, links: latestLinks };
    }

    const pendingRuns = pendingRunTargets(options.runs, latestLinks);
    if (pendingRuns.length === 0) {
      return { status: "completed", links: latestLinks };
    }

    if (attempt < maxAttempts - 1) {
      await sleep(intervalMs);
    }
  }

  return {
    status: "timeout",
    pendingRuns: pendingRunTargets(options.runs, latestLinks),
    links: latestLinks
  };
}

function missingRunTargets(runs: LinkRunTarget[], links: LinkView[]): LinkRunTarget[] {
  return runs.filter((run) => !links.some((link) => link.id === run.linkId));
}

function pendingRunTargets(runs: LinkRunTarget[], links: LinkView[]): LinkRunTarget[] {
  return runs.filter((run) => {
    const link = links.find((item) => item.id === run.linkId);
    return !link || link.lastCheckRunId !== run.runId;
  });
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
